import React from "react";

// Renders Graphics Interpretation charts as inline SVG from question data.
// chart: { type: "bar" | "groupedBar" | "line" | "pie", title, unit, yLabel, labels, series: [{ name, values }] }

const W = 560;
const H = 320;
const PAD = { top: 44, right: 24, bottom: 52, left: 64 };
const COLORS = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#14b8a6"];

const fmt = (v, unit = "") => {
  const n = Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1);
  if (unit === "$") return `$${n}`;
  return unit ? `${n}${unit}` : n;
};

const niceMax = (max) => {
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  for (const s of steps) if (s * pow >= max * 1.1) return s * pow;
  return 10 * pow;
};

function Axes({ maxY, unit, yLabel, labels, xFor }) {
  const plotH = H - PAD.top - PAD.bottom;
  const ticks = 5;
  return (
    <g>
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = (maxY / ticks) * i;
        const y = PAD.top + plotH - (v / maxY) * plotH;
        return (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="#e5e7eb" />
            <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
              {fmt(Number(v.toFixed(2)), unit)}
            </text>
          </g>
        );
      })}
      <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke="#9ca3af" />
      {labels.map((l, i) => (
        <text key={l} x={xFor(i)} y={H - PAD.bottom + 18} textAnchor="middle" fontSize="12" fill="#374151">{l}</text>
      ))}
      {yLabel && (
        <text x={16} y={PAD.top + plotH / 2} fontSize="11" fill="#6b7280" textAnchor="middle"
          transform={`rotate(-90 16 ${PAD.top + plotH / 2})`}>{yLabel}</text>
      )}
    </g>
  );
}

function Legend({ series }) {
  if (series.length < 2) return null;
  return (
    <g>
      {series.map((s, i) => (
        <g key={s.name} transform={`translate(${PAD.left + i * 140}, ${H - 14})`}>
          <rect width="12" height="12" y="-10" fill={COLORS[i]} rx="2" />
          <text x="18" fontSize="12" fill="#374151">{s.name}</text>
        </g>
      ))}
    </g>
  );
}

function BarChart({ chart }) {
  const { labels, series, unit, yLabel } = chart;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const maxY = niceMax(Math.max(...series.flatMap((s) => s.values)));
  const slot = plotW / labels.length;
  const barW = Math.min(48, (slot * 0.7) / series.length);
  const xFor = (i) => PAD.left + slot * i + slot / 2;
  return (
    <>
      <Axes maxY={maxY} unit={unit} yLabel={yLabel} labels={labels} xFor={xFor} />
      {series.map((s, si) =>
        s.values.map((v, i) => {
          const h = (v / maxY) * plotH;
          const x = xFor(i) - (barW * series.length) / 2 + barW * si;
          const y = PAD.top + plotH - h;
          return (
            <g key={`${si}-${i}`}>
              <rect x={x + 1} y={y} width={barW - 2} height={h} fill={COLORS[si]} rx="2" />
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="11" fill="#111827">{fmt(v, unit)}</text>
            </g>
          );
        })
      )}
      <Legend series={series} />
    </>
  );
}

function LineChart({ chart }) {
  const { labels, series, unit, yLabel } = chart;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const maxY = niceMax(Math.max(...series.flatMap((s) => s.values)));
  const slot = plotW / labels.length;
  const xFor = (i) => PAD.left + slot * i + slot / 2;
  const yFor = (v) => PAD.top + plotH - (v / maxY) * plotH;
  return (
    <>
      <Axes maxY={maxY} unit={unit} yLabel={yLabel} labels={labels} xFor={xFor} />
      {series.map((s, si) => (
        <g key={s.name}>
          <polyline fill="none" stroke={COLORS[si]} strokeWidth="2.5"
            points={s.values.map((v, i) => `${xFor(i)},${yFor(v)}`).join(" ")} />
          {s.values.map((v, i) => (
            <g key={i}>
              <circle cx={xFor(i)} cy={yFor(v)} r="4" fill="#fff" stroke={COLORS[si]} strokeWidth="2" />
              <text x={xFor(i)} y={yFor(v) - 9} textAnchor="middle" fontSize="11" fill="#111827">{fmt(v, unit)}</text>
            </g>
          ))}
        </g>
      ))}
      <Legend series={series} />
    </>
  );
}

function PieChart({ chart }) {
  const { labels, series, unit } = chart;
  const values = series[0].values;
  const total = values.reduce((a, b) => a + b, 0);
  const cx = 200, cy = H / 2 + 12, r = 110;
  let angle = -Math.PI / 2;
  return (
    <>
      {values.map((v, i) => {
        const a0 = angle;
        const a1 = angle + (v / total) * Math.PI * 2;
        angle = a1;
        const large = a1 - a0 > Math.PI ? 1 : 0;
        const p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
        const p1 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
        const mid = (a0 + a1) / 2;
        return (
          <g key={labels[i]}>
            <path d={`M${cx},${cy} L${p0[0]},${p0[1]} A${r},${r} 0 ${large} 1 ${p1[0]},${p1[1]} Z`}
              fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth="2" />
            <text x={cx + r * 0.62 * Math.cos(mid)} y={cy + r * 0.62 * Math.sin(mid) + 4}
              textAnchor="middle" fontSize="12" fontWeight="600" fill="#fff">{fmt(v, unit)}</text>
          </g>
        );
      })}
      {labels.map((l, i) => (
        <g key={l} transform={`translate(360, ${cy - 40 + i * 26})`}>
          <rect width="14" height="14" y="-11" fill={COLORS[i % COLORS.length]} rx="2" />
          <text x="22" fontSize="13" fill="#374151">{l}</text>
        </g>
      ))}
    </>
  );
}

export default function GmatChart({ chart }) {
  if (!chart) return null;
  const Body = chart.type === "pie" ? PieChart : chart.type === "line" ? LineChart : BarChart;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={chart.title}
      style={{ width: "100%", maxWidth: W, height: "auto", background: "#fff", border: "1px solid #d1d5db", borderRadius: 8 }}>
      <text x={W / 2} y={24} textAnchor="middle" fontSize="15" fontWeight="600" fill="#111827">{chart.title}</text>
      <Body chart={chart} />
    </svg>
  );
}
